import React, { useEffect, useState } from "react";
import classes from "../../components/Application/ApplicationCard/style/ApplicationCard.module.css";
import {
  UI_ITOModuleTitle,
  UI_ModuleStikeController,
} from "../../UI_ITO/indexUI";
import { Alert, Button, Space, Spin, Tag } from "antd";
import { useParams } from "react-router-dom";
import { ApplicationTabs } from "../../components/Application/ApplicationDetails/ApplicationTabs/ApplicationTabs";
import { useAppDispatch, useAppSelector } from "../../store/reduxHooks";
import {
  useGetApplicationDataToBankQuery,
  useSetApplicationBankEmployeeMutation,
  useSetApplicationBankStatusMutation,
} from "../../store/api/applicationBankApi/applicationBank.api";
import {
  CLEAR_APPLICATION_DATA_TO_BANK,
  CLOSE_SIGN_INFO_MODAL,
  SET_APPLICATION_DATA_TO_BANK,
} from "../../store/slices/applicationBankSlice/applicationBankSlice";
import { USER } from "../../types";
import { Guard } from "../../utils/guards/Guard";
import { APPLICATION_BANK_STATUS } from "../../utils/guards/ApplicationBankStatusGuard/types";
import { UnderwriterChangeModal } from "../../components/Application/UnderwriterChangeModal/UnderwriterChangeModal";

import buttonClasses from "./PageApplication.module.css";
import { getFullName } from "../../utils/utilities/getFullName";
import { SetApplicationStatusAction } from "../../store/api/applicationBankApi/config/config";
import { DocumentsSignModalButton } from "../../components/Application/DocumentsSignModal/ui/DocumentsSignModalButton";
import { useGetPermissionQuery } from "../../store/api/permission.api";
import { SignInfoModal } from "../../components/Application/SignInfoModal/ui/SignInfoModal";
import { SELECT_SIGN_INFO_MODAL } from "../../store/selectors/applicationDataToBankSelector";
import { useSendDataToRepeatedScoringMutation } from "../../store/api/applicationBankApi/scoring.api";
import { BankEmployeeChangeModal } from "../../components/Application/BankEmployeeChangeModal/BankEmployeeChangeModal";
import { OPEN_REFUSE_MODAL } from "../../store/slices/modalStatesSlice/modalStatesSlice";
import { RefuseVariantsModal } from "../../components/Application/RefuseVariantsModal/RefuseVariantsModal";

export const PageApplication = () => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBankEmployeeChangeModalOpen, setIsBankEmployeeChangeModalOpen] =
    useState(false);
  const { applicationBankId } = useParams();

  const signInfoModalState = useAppSelector(SELECT_SIGN_INFO_MODAL);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const showBankEmployeeChangeModal = () => {
    setIsBankEmployeeChangeModalOpen(true);
  };

  const closeBankEmployeeChangeModal = () => {
    setIsBankEmployeeChangeModalOpen(false);
  };

  const closeSignInfoModal = () => {
    dispatch(CLOSE_SIGN_INFO_MODAL());
  };

  const {
    data: applicationBankdata,
    isLoading,
    isSuccess,
  } = useGetApplicationDataToBankQuery(Number(applicationBankId));

  const [setApplicationBankStatus, { isLoading: isChangeStatusLoading }] =
    useSetApplicationBankStatusMutation();
  const [sendToRepeatedScoring, { isLoading: isLoadingRepeatedScoring }] =
    useSendDataToRepeatedScoringMutation();
  const { data: permissionData } = useGetPermissionQuery(undefined);
  const [setApplicationBankEmployee, { isLoading: isSetBankEmployeeLoading }] =
    useSetApplicationBankEmployeeMutation();

  const allowCnangingBankEmployee =
    (applicationBankdata?.employee_user?.id === permissionData?.data.user.id &&
      permissionData?.data.user.type === USER.EMPLOYEE) ||
    permissionData?.data.user.type === USER.LEAD;

  const changeStatus = (action: SetApplicationStatusAction) => {
    if (applicationBankId) {
      setApplicationBankStatus({
        applicationBankId: +applicationBankId,
        action,
      });
    }
  };

  const takeInWorkByBankEmployee = () => {
    if (permissionData?.data.user.type === USER.EMPLOYEE) {
      setApplicationBankEmployee({
        applicationBankId: Number(applicationBankId),
        bankEmployeeId: Number(permissionData?.data.user.id),
      });
    }
  };

  const sendToScoring = () => {
    if (applicationBankId)
      sendToRepeatedScoring({ applicationBankId: Number(applicationBankId) });
  };

  const responsibleUsers = [
    {
      position: "Андеррайтер",
      fullName: applicationBankdata?.responsible_user
        ? getFullName(applicationBankdata?.responsible_user)
        : "-",
      id: applicationBankdata?.responsible_user?.id,
    },
    {
      position: "Руководитель",
      fullName: applicationBankdata?.lead_user
        ? getFullName(applicationBankdata?.lead_user)
        : "-",
      id: applicationBankdata?.lead_user?.id,
    },
    {
      position: "Сотрудник сопровождения",
      fullName: applicationBankdata?.employee_user
        ? getFullName(applicationBankdata?.employee_user)
        : "-",
      id: applicationBankdata?.employee_user?.id,
    },
  ];

  useEffect(() => {
    if (applicationBankdata) {
      dispatch(SET_APPLICATION_DATA_TO_BANK(applicationBankdata));
    }
    return () => {
      dispatch(CLEAR_APPLICATION_DATA_TO_BANK());
    };
  }, [applicationBankdata, dispatch]);

  const isDisabled = () => {
    return !applicationBankdata?.scoring_result.data.creditworthiness;
  };

  const isOfferDocumentsUploaded = () => {
    return !!applicationBankdata?.offer.every(
      (document) => !!document?.document?.length
    );
  };

  return (
    <Spin spinning={isLoading} size="large">
      <div className={classes.applicationWrapper}>
        <div className={classes.applicationTitleContainer}>
          <UI_ITOModuleTitle title={`Заявка № ${applicationBankId ?? 0}`} />
          <Tag color={applicationBankdata?.modified_bank_status.color}>
            {applicationBankdata?.modified_bank_status.title}
          </Tag>
        </div>
        <div className={classes.responsibleUsersWrapper}>
          {responsibleUsers.map((user) => {
            if (user.id) {
              return (
                <div key={user.id} className={classes.responsibleUser}>
                  <p className={classes.responsibleUserPosition}>
                    {user.position}:
                  </p>
                  <p className={classes.responsibleUserFullName}>
                    {user.fullName}
                  </p>
                </div>
              );
            }
          })}
        </div>
        {applicationBankdata?.refusal_info && (
          <Alert
            style={{ marginTop: "10px" }}
            type="error"
            message={`Причина отказа: ${applicationBankdata.refusal_info.text}`}
          />
        )}
        <ApplicationTabs />
      </div>
      <UnderwriterChangeModal isOpen={isModalOpen} hideModal={closeModal} />
      {signInfoModalState.queryParams && (
        <SignInfoModal
          queryParams={signInfoModalState.queryParams}
          isOpened={signInfoModalState.isOpened}
          onClose={closeSignInfoModal}
        />
      )}
      <BankEmployeeChangeModal
        isOpen={isBankEmployeeChangeModalOpen}
        hideModal={closeBankEmployeeChangeModal}
      />

      {applicationBankdata && isSuccess && (
        <>
          <UI_ModuleStikeController position="right">
            <Space>
              <Guard.Role roles={[USER.UNDERWRITER]}>
                <Space>
                  {/* Кнопка смена андеррайтера */}
                  <Guard.ApplicationBankStatus
                    status={applicationBankdata.modified_bank_status.type}
                    disabledStatuses={[
                      APPLICATION_BANK_STATUS.NEW_APPLICATION,
                      APPLICATION_BANK_STATUS.PROCESSED_BY_LEAD,
                      APPLICATION_BANK_STATUS.SIGNED_MANAGER,
                      APPLICATION_BANK_STATUS.DEAL_COMPLETED,
                    ]}
                  >
                    <Button
                      onClick={showModal}
                      className={buttonClasses.inWork}
                    >
                      Назначить андеррайтера
                    </Button>
                  </Guard.ApplicationBankStatus>
                  {/* Кнопки на статусе Новая заявка */}
                  <Guard.ApplicationBankStatus
                    status={applicationBankdata.modified_bank_status.type}
                    allowedStatuses={[APPLICATION_BANK_STATUS.NEW_APPLICATION]}
                  >
                    <Button
                      type="primary"
                      onClick={() => changeStatus("take")}
                      loading={isChangeStatusLoading}
                    >
                      Взять в работу
                    </Button>
                  </Guard.ApplicationBankStatus>

                  {/* Кнопки на статусе в Работе и На доработке*/}
                  <Guard.ApplicationBankStatus
                    status={applicationBankdata.modified_bank_status.type}
                    allowedStatuses={[
                      APPLICATION_BANK_STATUS.DIRECTED,
                      APPLICATION_BANK_STATUS.REVISION,
                      APPLICATION_BANK_STATUS.PAID,
                    ]}
                  >
                    <Space>
                      <Button
                        type="default"
                        danger
                        onClick={() => dispatch(OPEN_REFUSE_MODAL())}
                        loading={isChangeStatusLoading}
                      >
                        Отказать
                      </Button>

                      <Guard.ApplicationBankStatus
                        status={applicationBankdata.modified_bank_status.type}
                        allowedStatuses={[APPLICATION_BANK_STATUS.DIRECTED]}
                      >
                        <Button
                          type="default"
                          onClick={() => changeStatus("revision")}
                          loading={isChangeStatusLoading}
                          className={buttonClasses.inWork}
                        >
                          На доработку
                        </Button>
                      </Guard.ApplicationBankStatus>

                      <Guard.ApplicationBankStatus
                        status={applicationBankdata.modified_bank_status.type}
                        allowedStatuses={[
                          APPLICATION_BANK_STATUS.DIRECTED,
                          APPLICATION_BANK_STATUS.REVISION,
                        ]}
                      >
                        <Button
                          type={"primary"}
                          onClick={() => changeStatus("approve")}
                          loading={isChangeStatusLoading}
                        >
                          Предварительно одобрить
                        </Button>
                      </Guard.ApplicationBankStatus>
                    </Space>
                  </Guard.ApplicationBankStatus>

                  <Guard.ApplicationBankStatus
                    status={applicationBankdata.modified_bank_status.type}
                    allowedStatuses={[
                      APPLICATION_BANK_STATUS.AWAITING_PAYMENT,
                      APPLICATION_BANK_STATUS.PAID,
                      APPLICATION_BANK_STATUS.SIGNATURE_REQUIRED,
                    ]}
                  >
                    <Button
                      type={"primary"}
                      onClick={() => sendToScoring()}
                      disabled={isDisabled()}
                      loading={
                        isChangeStatusLoading || isLoadingRepeatedScoring
                      }
                    >
                      Повторный скоринг
                    </Button>
                  </Guard.ApplicationBankStatus>

                  {/* Кнопки на статусе Предварительно одобрена */}
                  <Guard.ApplicationBankStatus
                    status={applicationBankdata.modified_bank_status.type}
                    allowedStatuses={[APPLICATION_BANK_STATUS.OFFER]}
                  >
                    <Button
                      type="primary"
                      onClick={() => changeStatus("send-offer")}
                      disabled={
                        !isOfferDocumentsUploaded() || isChangeStatusLoading
                      }
                      loading={isChangeStatusLoading}
                    >
                      Отправить предложение
                    </Button>
                  </Guard.ApplicationBankStatus>

                  {/* Кнопки на статусе Подтвердить оплату */}
                  <Guard.ApplicationBankStatus
                    status={applicationBankdata.modified_bank_status.type}
                    allowedStatuses={[APPLICATION_BANK_STATUS.AWAITING_PAYMENT]}
                  >
                    <Button
                      type="primary"
                      onClick={() => changeStatus("confirm-payment")}
                      loading={isChangeStatusLoading}
                    >
                      Подтвердить оплату
                    </Button>
                  </Guard.ApplicationBankStatus>

                  {/* Кнопки на статусе Ожидает подписи руководителя */}
                  <Guard.ApplicationBankStatus
                    status={applicationBankdata.modified_bank_status.type}
                    allowedStatuses={[
                      APPLICATION_BANK_STATUS.AWAITING_SIGNATURE,
                    ]}
                  >
                    <Button
                      type="primary"
                      onClick={() => changeStatus("send-lead-signature")}
                      loading={isChangeStatusLoading}
                    >
                      Отправить на подписание руководителю
                    </Button>
                  </Guard.ApplicationBankStatus>
                </Space>
              </Guard.Role>

              {allowCnangingBankEmployee && (
                <Button
                  onClick={showBankEmployeeChangeModal}
                  className={buttonClasses.inWork}
                >
                  Назначить сотрудника сопровождения
                </Button>
              )}

              <Guard.Role roles={[USER.LEAD]}>
                {/* Кнопка смена андеррайтера */}
                <Guard.ApplicationBankStatus
                  status={applicationBankdata.modified_bank_status.type}
                  disabledStatuses={[
                    APPLICATION_BANK_STATUS.PROCESSED_BY_LEAD,
                    APPLICATION_BANK_STATUS.SIGNED_MANAGER,
                    APPLICATION_BANK_STATUS.DEAL_COMPLETED,
                  ]}
                >
                  <Button onClick={showModal} className={buttonClasses.inWork}>
                    Назначить андеррайтера
                  </Button>
                </Guard.ApplicationBankStatus>

                {/* Кнопки на статусе Ожидает подписи руководителя */}
                <Guard.ApplicationBankStatus
                  status={applicationBankdata.modified_bank_status.type}
                  allowedStatuses={[APPLICATION_BANK_STATUS.PROCESSED_BY_LEAD]}
                >
                  <DocumentsSignModalButton
                    applicationBankId={Number(applicationBankId)}
                  />
                </Guard.ApplicationBankStatus>
              </Guard.Role>
              <Guard.Role roles={[USER.EMPLOYEE]}>
                {applicationBankdata.employee_user?.id ===
                permissionData?.data?.user?.id ? (
                  <Guard.ApplicationBankStatus
                    status={applicationBankdata.modified_bank_status.type}
                    allowedStatuses={[APPLICATION_BANK_STATUS.SIGNED_MANAGER]}
                  >
                    <Button
                      type="primary"
                      onClick={() => changeStatus("release-application")}
                      disabled={
                        !applicationBankdata.number_register ||
                        (applicationBankdata.register &&
                          !applicationBankdata.register[0].document?.length)
                      }
                      loading={isChangeStatusLoading}
                    >
                      Выпустить заявку
                    </Button>
                  </Guard.ApplicationBankStatus>
                ) : (
                  <Guard.ApplicationBankStatus
                    status={applicationBankdata.modified_bank_status.type}
                    allowedStatuses={[APPLICATION_BANK_STATUS.SIGNED_MANAGER]}
                  >
                    <Button
                      type="primary"
                      onClick={() => takeInWorkByBankEmployee()}
                      loading={isSetBankEmployeeLoading}
                    >
                      Взять в работу
                    </Button>
                  </Guard.ApplicationBankStatus>
                )}
              </Guard.Role>
            </Space>
          </UI_ModuleStikeController>
          <RefuseVariantsModal applicationBankId={Number(applicationBankId)} />
        </>
      )}
    </Spin>
  );
};
